import { useState } from "react";
import { css } from "styled-system/css";
import { fmtFixed } from "~/lib/format";
import { ed, tbl } from "~/components/editorial";

interface ActionItemAuthor {
  id: string;
  name: string;
  avatarUrl: string | null;
}

interface ActionItemPR {
  id: string;
  title: string;
  number: number;
  author: ActionItemAuthor | null;
  url: string;
  daysOpen?: number;
  hoursWaiting?: number;
  linesChanged?: number;
  daysSinceRequest?: number;
}

interface ActionItemsProps {
  stalePrs: ActionItemPR[];
  waitingForReview: ActionItemPR[];
  largePrs: ActionItemPR[];
  changesRequested: ActionItemPR[];
}

type SectionType = "stale" | "waitingReview" | "large" | "changesRequested";

interface SectionConfig {
  key: SectionType;
  title: string;
  /** What the number column means for this group. */
  measure: string;
  items: ActionItemPR[];
  value: (pr: ActionItemPR) => string;
}

/**
 * Open PRs that need someone, grouped by why, as ruled lists. Order is
 * by urgency (changes requested first); no severity colors.
 */
export function ActionItems({
  stalePrs,
  waitingForReview,
  largePrs,
  changesRequested,
}: ActionItemsProps) {
  const [expanded, setExpanded] = useState<Set<SectionType>>(
    new Set(["stale", "changesRequested"]),
  );

  const toggle = (section: SectionType) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(section)) next.delete(section);
      else next.add(section);
      return next;
    });

  const sections: SectionConfig[] = [
    {
      key: "changesRequested",
      title: "Changes requested",
      measure: "Since request",
      items: changesRequested,
      value: (pr) =>
        pr.daysSinceRequest !== undefined ? `${pr.daysSinceRequest}d` : "–",
    },
    {
      key: "stale",
      title: "Stale, open 7+ days",
      measure: "Open",
      items: stalePrs,
      value: (pr) => (pr.daysOpen !== undefined ? `${pr.daysOpen}d` : "–"),
    },
    {
      key: "waitingReview",
      title: "Waiting 24+ hours for review",
      measure: "Waiting",
      items: waitingForReview,
      value: (pr) =>
        pr.hoursWaiting === undefined
          ? "–"
          : pr.hoursWaiting < 24
            ? `${pr.hoursWaiting}h`
            : `${Math.floor(pr.hoursWaiting / 24)}d`,
    },
    {
      key: "large",
      title: "Large, 500+ lines",
      measure: "Lines",
      items: largePrs,
      value: (pr) =>
        pr.linesChanged !== undefined ? fmtFixed(pr.linesChanged) : "–",
    },
  ];

  const withItems = sections.filter((s) => s.items.length > 0);

  if (withItems.length === 0) {
    return (
      <p className={ed.proseLinked}>
        No open PR is stale, oversized, or waiting on a reviewer.
      </p>
    );
  }

  return (
    <div className={styles.groups}>
      {withItems.map((section) => {
        const open = expanded.has(section.key);
        return (
          <section key={section.key} className={styles.group}>
            <button
              type="button"
              className={styles.groupHead}
              onClick={() => toggle(section.key)}
              aria-expanded={open}
            >
              <span className={styles.groupTitle}>{section.title}</span>
              <span className={styles.groupCount}>
                {fmtFixed(section.items.length)}
              </span>
              <span className={styles.groupToggle}>
                {open ? "Hide" : "Show"}
              </span>
            </button>
            {open && (
              <table className={tbl.numeric}>
                <thead>
                  <tr>
                    <th data-num>PR</th>
                    <th>Title</th>
                    <th>Author</th>
                    <th data-num>{section.measure}</th>
                  </tr>
                </thead>
                <tbody>
                  {section.items.map((item) => (
                    <tr key={item.id}>
                      <td data-num data-dim className={styles.numCol}>
                        #{item.number}
                      </td>
                      <td data-text className={styles.titleCol}>
                        <a
                          href={item.url}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {item.title}
                        </a>
                      </td>
                      <td data-text className={styles.authorCol}>
                        {item.author?.name ?? "Unknown"}
                      </td>
                      <td data-num className={styles.valCol}>
                        {section.value(item)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </section>
        );
      })}
    </div>
  );
}

const styles = {
  groups: css({ display: "flex", flexDirection: "column", gap: "18px" }),
  group: css({ borderTopWidth: "2px", borderTopStyle: "solid", borderColor: "ed.ink" }),
  groupHead: css({
    all: "unset",
    boxSizing: "border-box",
    display: "flex",
    alignItems: "baseline",
    gap: "10px",
    width: "100%",
    py: "6px",
    cursor: "pointer",
    _focusVisible: { outline: "1px solid var(--colors-ed-ink)" },
    "&:hover span:last-child": { textDecoration: "underline" },
  }),
  groupTitle: css({
    fontFamily: "sans",
    fontSize: "12px",
    fontWeight: 700,
    letterSpacing: "0.08em",
    textTransform: "uppercase",
    color: "ed.ink",
  }),
  groupCount: css({
    fontFamily: "mono",
    fontSize: "13px",
    fontWeight: 600,
    color: "ed.ink",
  }),
  groupToggle: css({
    ml: "auto",
    fontFamily: "sans",
    fontSize: "11px",
    color: "ed.ink3",
  }),
  numCol: css({ width: "64px" }),
  titleCol: css({ width: "100%" }),
  authorCol: css({ whiteSpace: "nowrap!", color: "ed.ink2!" }),
  valCol: css({ width: "80px" }),
};
